import { Link, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SettingsContact, SettingsSample } from '../sampleData';
import type { ChangeEntry, ChangeItem } from '../state';
import { formatDay, formatDayMonth, formatRange } from '../state/formatDates';

import { cachedDateFormat } from '@/lib/dateLocale';

/**
 * What's changed (brief §3): the saved changes, newest first — the card (a
 * link to it), when, what changed, and who. Five at first, then "Show all".
 * Every change in this prototype is made in this browser, so "who" is "You".
 * Typed as Web's `ui/ProjectActivityRow`: the name in regular navy, the
 * secondary facts in the plain label variant — not mono, not bold.
 */
export type ChangeListProps = {
  changes: ChangeEntry[];
  sample: SettingsSample;
  contacts: SettingsContact[];
  /** The card's title, by its id. */
  cardTitle: (card: ChangeEntry['card']) => string;
  onGo: (card: ChangeEntry['card']) => void;
  /** "now", injected so the list is testable. */
  now?: Date;
};

const SHOWN = 5;

/** The words for a role or a field, as the cards already say them. */
const ROLE_KEY = {
  projectManager: 'people.ProjectManager',
  accountManager: 'people.AccountManager',
  partner: 'people.Partner',
  contact: 'people.Contact',
} as const;
const FIELD_KEY = {
  legalEntity: 'belongs.LegalEntity',
  department: 'belongs.Department',
  projectType: 'belongs.ProjectType',
  category: 'belongs.Category',
} as const;
const timeFormat = cachedDateFormat({ hour: '2-digit', minute: '2-digit' });
const localDay = (date: Date) =>
  `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const ChangeList = ({
  changes,
  sample,
  contacts,
  cardTitle,
  onGo,
  now = new Date(),
}: ChangeListProps) => {
  const { t } = useTranslation('pmDetails');
  const [all, setAll] = useState(false);
  const shown = all ? changes : changes.slice(0, SHOWN);

  const person = (id: string | null) =>
    id === null
      ? t('change.None')
      : (sample.employees.find((employee) => employee.id === id)?.name ??
        contacts.find((contact) => contact.id === id)?.name ??
        t('change.None'));
  const organisation = (field: string, value: string | null) => {
    if (value === null) return t('change.None');
    if (field === 'legalEntity')
      return sample.legalEntities.find((e) => e.id === value)?.name ?? value;
    if (field === 'department')
      return sample.departments.find((d) => d.id === value)?.name ?? value;
    return value;
  };

  const text = (item: ChangeItem): string => {
    switch (item.kind) {
      case 'descriptionUpdated':
      case 'descriptionRemoved':
        return t(`change.${item.kind}`);
      case 'fileAdded':
      case 'fileRemoved':
      case 'linkAdded':
      case 'linkRemoved':
        return t(`change.${item.kind}`, { name: item.name });
      case 'person':
        return t('change.FromTo', {
          field: t(ROLE_KEY[item.role]),
          from: person(item.from),
          to: person(item.to),
        });
      case 'canLogTime':
        return t('change.canLogTime', { before: item.before, after: item.after });
      case 'askForEtc':
        return item.on ? t('change.etcOn') : t('change.etcOff');
      case 'period':
        return t('change.period', {
          range:
            item.startDate && item.endDate
              ? formatRange(item.startDate, item.endDate)
              : t('change.None'),
        });
      case 'delivery':
        return item.date === ''
          ? t('change.deliveryRemoved')
          : t('change.delivery', { date: formatDay(item.date) });
      case 'organisation':
        return t('change.FromTo', {
          field: t(FIELD_KEY[item.field]),
          from: organisation(item.field, item.from),
          to: organisation(item.field, item.to),
        });
      case 'planner':
        return item.on ? t('change.plannerOn') : t('change.plannerOff');
      case 'likelihood':
        return t('change.likelihood', { from: item.from, to: item.to });
    }
  };

  const when = (iso: string) => {
    const at = new Date(iso);
    return localDay(at) === localDay(now)
      ? t('panel.Today', { time: timeFormat().format(at) })
      : formatDayMonth(localDay(at));
  };

  return (
    <Stack sx={{ gap: 1 }} data-automation-id="details-whats-changed">
      {changes.length === 0 ? (
        <Typography variant="bodyRegular" sx={{ color: 'var(--color-fog-80)' }}>
          {t('panel.NoChanges')}
        </Typography>
      ) : (
        <Stack component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {shown.map((entry) => (
            <Stack
              component="li"
              key={entry.id}
              data-automation-id="details-change"
              sx={{
                gap: 0.5,
                py: 1.5,
                borderTop: 'var(--border-width-thin) solid var(--color-fog-20)',
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => onGo(entry.card)}
                  underline="hover"
                  sx={{
                    flex: 1,
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: 'var(--font-size-body-regular)',
                    color: 'var(--color-navy-100)',
                  }}
                >
                  {cardTitle(entry.card)}
                </Link>
                <Typography variant="label" sx={{ color: 'var(--color-fog-80)' }}>
                  {when(entry.at)}
                </Typography>
              </Stack>
              {entry.items.map((item, index) => (
                <Typography
                  key={index}
                  variant="bodyRegular"
                  data-automation-id="details-change-item"
                >
                  {text(item)}
                </Typography>
              ))}
              <Typography variant="label" sx={{ color: 'var(--color-fog-80)' }}>
                {t('panel.You')}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      {changes.length > SHOWN ? (
        <Link
          component="button"
          type="button"
          onClick={() => setAll((open) => !open)}
          sx={{
            alignSelf: 'flex-start',
            font: 'inherit',
            fontSize: 'var(--font-size-body-regular)',
          }}
          data-automation-id="details-changes-toggle"
        >
          {all ? t('panel.ShowFewer') : t('panel.ShowAll', { count: changes.length })}
        </Link>
      ) : null}
    </Stack>
  );
};
